/**
 * BrokenPage — the friendly, on-brand replacement for cold "page not found" /
 * "something went wrong" fallbacks across the taker. Captures the broken URL,
 * referrer, and UA automatically; lets the user add free-text + an optional
 * email; submits a ticket so the team can fix it for the next person.
 *
 * Used by main.tsx catch-all, App.tsx error phase, BookingPage, BookingConfirmedPage,
 * LetsTalkPage, ResultsPage, SampleResultPage, CheckoutPage, CohortPage, ReplayPage.
 */
import { useEffect, useMemo, useState } from 'react';
import { BRAND_NAME, BRAND_HOME_URL, BRAND_ACCENT, BRAND_LOGO_URL } from '../branding';

const API = import.meta.env.VITE_API_BASE_URL as string;

export type BrokenVariant = '404' | 'error' | 'gone';

export interface BrokenPageProps {
  variant?: BrokenVariant;
  /** Defaults to window.location.href. */
  attemptedUrl?: string;
  /** Optional override of the empathy paragraph. */
  message?: string;
  /** Hide the report-form section. Useful for surfaces where follow-up isn't needed. */
  hideForm?: boolean;
  /** The raw error behind an 'error' page, recorded on the ticket so triage can
   *  tell a passing failure from a real one. Never shown to the visitor. */
  errorDetail?: string;
}

const COPY: Record<BrokenVariant, { headline: string; empathy: string }> = {
  '404': {
    headline: 'This link took a wrong turn',
    empathy:
      "Looks like this link is broken — and that's on us, not you. We'd love to make this right and fix it for the next person too.",
  },
  error: {
    headline: "Something didn't work right",
    empathy:
      "Something on our end isn't behaving. A refresh usually clears it — if it persists, let us know.",
  },
  gone: {
    headline: 'This page has moved on',
    empathy:
      "What you're looking for has been retired or replaced. Tell us what you were after and we'll point you to the right spot.",
  },
};

export default function BrokenPage({
  variant = '404',
  attemptedUrl,
  message,
  hideForm = false,
  errorDetail,
}: BrokenPageProps) {
  const url = attemptedUrl ?? (typeof window !== 'undefined' ? window.location.href : '');
  const referrer = typeof document !== 'undefined' ? document.referrer : '';
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

  const copy = COPY[variant];
  const empathy = message ?? copy.empathy;

  const [showForm, setShowForm] = useState(false);
  const [userText, setUserText] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isPreview = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('preview');
  // A 'wrong turn' or 'moved on' address may have a redirect waiting in the
  // Engine's redirect table. Taker pages never pass through the worker, so we
  // ask before showing the page (mct_689da539-a13). 'error' pages are live
  // addresses that failed; they are never redirected.
  const [checkingRedirect, setCheckingRedirect] = useState(variant !== 'error' && !isPreview && !attemptedUrl);
  const ticketDetail = variant === 'error' ? (errorDetail ?? message ?? null) : null;

  useEffect(() => {
    document.title = `${copy.headline} — ${BRAND_NAME}`;
  }, [copy.headline]);

  // Set the page background to a soft slate so the card pops on light theme.
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = '#F8FAFC';
    return () => {
      document.body.style.background = prev;
    };
  }, []);

  useEffect(() => {
    if (!checkingRedirect) return;
    let done = false;
    const giveUp = window.setTimeout(() => { if (!done) { done = true; setCheckingRedirect(false); } }, 2500);
    const qs = new URLSearchParams({
      path: window.location.pathname,
      host: window.location.host,
      search: window.location.search,
    });
    fetch(`${API}/api/public/redirects/resolve?${qs.toString()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { target?: string | null } | null) => {
        if (done) return;
        done = true;
        window.clearTimeout(giveUp);
        if (data?.target) window.location.replace(data.target);
        else setCheckingRedirect(false);
      })
      .catch(() => {
        if (done) return;
        done = true;
        window.clearTimeout(giveUp);
        setCheckingRedirect(false);
      });
    return () => window.clearTimeout(giveUp);
  }, [checkingRedirect]);

  const reportSilently = useMemo(() => async () => {
    // Admin-side preview URLs (?preview=error etc.) deliberately render this
    // page to audit copy. They are not real user errors — never log a ticket.
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('preview')) {
      return;
    }
    // Fire-and-forget on mount so even users who don't fill the form are
    // counted (deduped server-side via fingerprint + 1hr window).
    try {
      await fetch(`${API}/api/public/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'broken-page',
          url,
          referrer,
          userAgent,
          pageVariant: variant,
          errorMessage: ticketDetail,
        }),
      });
    } catch {
      // Silent — we don't want the broken page to throw a second error.
    }
  }, [url, referrer, userAgent, variant, ticketDetail]);

  useEffect(() => {
    if (checkingRedirect) return;
    void reportSilently();
  }, [reportSilently, checkingRedirect]);

  async function submitReport(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/public/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'broken-page',
          url,
          referrer,
          userAgent,
          userText: userText.trim() || null,
          userEmail: userEmail.trim() || null,
          pageVariant: variant,
          errorMessage: ticketDetail,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error || `HTTP ${res.status}`);
      }
      setSubmitted(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Something went wrong';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  if (checkingRedirect) return <main style={pageStyle} aria-busy="true" />;

  return (
    <main style={pageStyle}>
      <div style={topBarStyle}>
        <a href={BRAND_HOME_URL} style={logoLinkStyle} aria-label={BRAND_NAME}>
          <img src={BRAND_LOGO_URL} alt={BRAND_NAME} style={logoImgStyle} />
        </a>
      </div>

      <div style={shellStyle}>
        <div style={cardStyle}>
          <div style={glyphWrapStyle}>
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <circle cx="24" cy="24" r="22" stroke="#FCA5A5" strokeWidth="2" fill="#FEF2F2" />
              <path d="M24 14V26" stroke="#B91C1C" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="24" cy="32" r="1.5" fill="#B91C1C" />
            </svg>
          </div>

          <h1 style={headlineStyle}>{copy.headline}</h1>
          <p style={empathyStyle}>{empathy}</p>

          {!hideForm && !submitted && (
            <>
              {!showForm ? (
                <>
                  {variant === 'error' ? (
                    <div style={errorCtaStackStyle}>
                      <button
                        type="button"
                        onClick={() => window.location.reload()}
                        style={primaryBtnStyle}
                      >
                        Try again
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowForm(true)}
                        style={secondaryBtnStyle}
                      >
                        Still not working? Tell us what happened
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowForm(true)}
                      style={primaryBtnStyle}
                    >
                      Tell us what happened
                    </button>
                  )}
                </>
              ) : (
                <form onSubmit={submitReport} style={formStyle}>
                  <p style={formHeadStyle}>Mind telling us what you were looking for?</p>

                  <label style={fieldLabelStyle}>
                    What were you trying to do?
                    <textarea
                      style={textareaStyle}
                      rows={3}
                      value={userText}
                      onChange={(e) => setUserText(e.target.value)}
                      placeholder="A few words is plenty — what page were you trying to reach?"
                      maxLength={2000}
                    />
                  </label>

                  <label style={fieldLabelStyle}>
                    Email so we can let you know once it's fixed (optional)
                    <input
                      type="email"
                      style={inputStyle}
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder="you@example.com"
                      maxLength={200}
                    />
                  </label>

                  {err && <p style={errStyle}>{err}</p>}

                  <button type="submit" disabled={busy} style={primaryBtnStyle}>
                    {busy ? 'Sending…' : "Report this — we'll fix it"}
                  </button>
                </form>
              )}
            </>
          )}

          {submitted && (
            <div style={thanksStyle}>
              <p style={thanksTextStyle}>
                Thanks. We've got it from here. If you left your email, we'll let you know once
                it's sorted.
              </p>
            </div>
          )}

          <a href={BRAND_HOME_URL} style={secondaryLinkStyle}>
            Take me to thesuccessfulbookkeeper.com →
          </a>
        </div>
      </div>
    </main>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#F8FAFC',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  display: 'flex',
  flexDirection: 'column',
  color: '#0F172A',
};

const topBarStyle: React.CSSProperties = {
  background: '#ffffff',
  borderBottom: '1px solid #E2E8F0',
  padding: '14px 24px',
};

const logoLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  textDecoration: 'none',
};

const logoImgStyle: React.CSSProperties = {
  height: 40,
  width: 'auto',
  display: 'block',
};

const shellStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '80px 20px 40px',
};

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: 16,
  padding: '40px 36px',
  maxWidth: 520,
  width: '100%',
  textAlign: 'center',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 18px 40px rgba(0,0,0,0.06)',
};

const glyphWrapStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 16,
};

const headlineStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: '#0F172A',
};

const empathyStyle: React.CSSProperties = {
  margin: '0 0 24px',
  fontSize: 15,
  lineHeight: 1.55,
  color: '#475569',
};

const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '12px 22px',
  background: BRAND_ACCENT,
  color: '#ffffff',
  border: 'none',
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  letterSpacing: '-0.005em',
};

const errorCtaStackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
};

const secondaryBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 13,
  fontWeight: 500,
  color: '#64748B',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textDecoration: 'underline',
  textDecorationColor: '#CBD5E1',
  textUnderlineOffset: 3,
};

const formStyle: React.CSSProperties = {
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  marginTop: 8,
};

const formHeadStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: '#0F172A',
  margin: 0,
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 13,
  fontWeight: 500,
  color: '#334155',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #CBD5E1',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  color: '#0F172A',
  resize: 'vertical',
  minHeight: 72,
  background: '#ffffff',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #CBD5E1',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  color: '#0F172A',
  background: '#ffffff',
};

const errStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#B91C1C',
  margin: 0,
};

const thanksStyle: React.CSSProperties = {
  background: '#ECFDF5',
  border: '1px solid #A7F3D0',
  borderRadius: 10,
  padding: '14px 16px',
  marginBottom: 8,
};

const thanksTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  color: '#065F46',
  lineHeight: 1.5,
};

const secondaryLinkStyle: React.CSSProperties = {
  display: 'inline-block',
  marginTop: 24,
  fontSize: 13,
  color: '#64748B',
  textDecoration: 'none',
};
